import {
  EMBRAER_REQUIRED_COLUMNS,
  buildEmbraerProgramacaoKey,
  collectEmbraerHorarios,
  inferTrechoFromOrigem,
  isEmbraerStatusRecusadoPelaCentral,
  mapEmbraerHeaders,
  normalizeEmbraerIdPedido,
  normalizeEmbraerNome,
  normalizeEmbraerTelefone,
  parseEmbraerDate,
  parseEmbraerHorario,
} from "./embraerColumns";

const MAX_EMBRAER_FILE_BYTES = 5 * 1024 * 1024;
const MAX_EMBRAER_ROWS = 5000;
const MAX_EMBRAER_COLUMNS = 80;
const ALLOWED_EMBRAER_EXTENSIONS = new Set(["xlsx", "xls", "xlsm"]);

const getFileExtension = (fileName = "") => {
  const match = String(fileName).toLowerCase().match(/\.([a-z0-9]+)$/);
  return match?.[1] || "";
};

const validateWorkbookFile = async (file) => {
  if (!file) throw new Error("Selecione uma planilha.");
  if (file.size > MAX_EMBRAER_FILE_BYTES) {
    throw new Error("A planilha excede o limite de 5 MB.");
  }

  const extension = getFileExtension(file.name);
  if (!ALLOWED_EMBRAER_EXTENSIONS.has(extension)) {
    throw new Error("Formato de planilha inválido. Use .xlsx, .xls ou .xlsm.");
  }

  const signature = new Uint8Array(await file.slice(0, 8).arrayBuffer());
  const isZipBased = signature[0] === 0x50 && signature[1] === 0x4b;
  const isOleBased = (
    signature[0] === 0xd0 &&
    signature[1] === 0xcf &&
    signature[2] === 0x11 &&
    signature[3] === 0xe0
  );

  if ((extension === "xlsx" || extension === "xlsm") && !isZipBased) {
    throw new Error("Arquivo .xlsx/.xlsm inválido ou corrompido.");
  }
  if (extension === "xls" && !isOleBased && !isZipBased) {
    throw new Error("Arquivo .xls inválido ou corrompido.");
  }
};

const assertMatrixLimits = (matrix) => {
  if (matrix.length > MAX_EMBRAER_ROWS + 1) {
    throw new Error(`A planilha excede o limite de ${MAX_EMBRAER_ROWS} linhas.`);
  }
  const oversizedRow = matrix.find((row) => Array.isArray(row) && row.length > MAX_EMBRAER_COLUMNS);
  if (oversizedRow) {
    throw new Error(`A planilha excede o limite de ${MAX_EMBRAER_COLUMNS} colunas.`);
  }
};

const cellValue = (row, index, xlsx, fieldKey) => {
  if (index == null || index < 0) return "";
  const value = row[index];
  if (value == null || value === "") return "";

  if (fieldKey === "dataViagem" && typeof value === "number" && xlsx?.SSF?.parse_date_code) {
    const parsed = xlsx.SSF.parse_date_code(value);
    if (parsed) {
      return `${parsed.y}-${String(parsed.m).padStart(2, "0")}-${String(parsed.d).padStart(2, "0")}`;
    }
  }

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    if (fieldKey === "dataViagem") return value.toISOString().slice(0, 10);
    if (fieldKey === "horario") return value.toTimeString().slice(0, 5);
  }

  return String(value).trim();
};

export const buildEmbraerRowsFromMatrix = (matrix, xlsx = null) => {
  if (!Array.isArray(matrix) || matrix.length < 2) {
    throw new Error("A planilha está vazia ou sem cabeçalho.");
  }

  const { mapping } = mapEmbraerHeaders(matrix[0]);
  const missing = EMBRAER_REQUIRED_COLUMNS.filter((key) => mapping[key] == null);
  if (missing.length) {
    throw new Error(`Colunas obrigatórias ausentes: ${missing.join(", ")}`);
  }

  const parsedLines = [];
  for (let lineIndex = 1; lineIndex < matrix.length; lineIndex += 1) {
    const row = matrix[lineIndex];
    if (!row || !row.length) continue;

    const data = parseEmbraerDate(cellValue(row, mapping.dataViagem, xlsx, "dataViagem"));
    const idPedido = normalizeEmbraerIdPedido(
      cellValue(row, mapping.numeroProgramacao, xlsx, "numeroProgramacao")
    );
    const nome = cellValue(row, mapping.nomePassageiro, xlsx, "nomePassageiro");
    if (!idPedido || !nome) continue;

    const tipoServico = cellValue(row, mapping.tipoServico, xlsx, "tipoServico");
    const origem = cellValue(row, mapping.origem, xlsx, "origem");
    const cidadeOrigem = mapping.cidadeOrigem != null
      ? cellValue(row, mapping.cidadeOrigem, xlsx, "cidadeOrigem")
      : "";
    const destino = cellValue(row, mapping.destino, xlsx, "destino");
    const cidadeDestino = mapping.cidadeDestino != null
      ? cellValue(row, mapping.cidadeDestino, xlsx, "cidadeDestino")
      : "";
    const trecho = inferTrechoFromOrigem({ origem, cidadeOrigem });
    const horario = parseEmbraerHorario(cellValue(row, mapping.horario, xlsx, "horario"));
    const statusProgramacao = mapping.statusProgramacao != null
      ? cellValue(row, mapping.statusProgramacao, xlsx, "statusProgramacao")
      : "";

    parsedLines.push({
      data,
      id_pedido: idPedido,
      tipo_servico: tipoServico,
      trecho,
      horario,
      status_programacao: statusProgramacao,
      programacao_key: buildEmbraerProgramacaoKey({
        id_pedido: idPedido,
        data,
        trecho,
      }),
      nome,
      nome_normalizado: normalizeEmbraerNome(nome),
      chapa: cellValue(row, mapping.chapa, xlsx, "chapa"),
      telefone: normalizeEmbraerTelefone(cellValue(row, mapping.telefone, xlsx, "telefone")),
      origem,
      cidade_origem: cidadeOrigem,
      destino,
      cidade_destino: cidadeDestino,
    });
  }

  const recusadoKeys = new Set(
    parsedLines
      .filter((line) => isEmbraerStatusRecusadoPelaCentral(line.status_programacao))
      .map((line) => line.programacao_key)
  );

  const rows = parsedLines
    .filter((line) => !recusadoKeys.has(line.programacao_key))
    .map((line) => {
      const row = { ...line };
      delete row.programacao_key;
      delete row.status_programacao;
      return row;
    });

  const totalLinhasIgnoradas = parsedLines.length - rows.length;

  if (!rows.length) {
    if (recusadoKeys.size > 0) {
      throw new Error("Todas as programações da planilha estão com status Recusado pela central.");
    }
    throw new Error("Nenhuma linha válida encontrada na planilha.");
  }

  return {
    rows,
    total_linhas_ignoradas: totalLinhasIgnoradas,
    programacoes_ignoradas: recusadoKeys.size,
  };
};

export const groupEmbraerRows = (rows = []) => {
  const map = new Map();

  for (const row of rows) {
    const key = buildEmbraerProgramacaoKey(row);

    if (!map.has(key)) {
      map.set(key, {
        key,
        id_pedido: row.id_pedido,
        data: row.data,
        horario: row.horario,
        horarios: [],
        trecho: row.trecho,
        tipo_servico: row.tipo_servico,
        passageiros: [],
      });
    }

    const group = map.get(key);
    if (!group.tipo_servico && row.tipo_servico) group.tipo_servico = row.tipo_servico;

    if (row.horario && !group.horarios.includes(row.horario)) {
      group.horarios.push(row.horario);
    }

    // Identidade = nome + chapa + telefone (mesma regra do parser do
    // playwright-service): homônimos com chapa/telefone distintos são
    // pessoas diferentes e precisam ser preservados.
    const identidade = (passageiro) => [
      passageiro.nome_normalizado,
      passageiro.chapa || "",
      passageiro.telefone || "",
    ].join("|");
    const rowIdentidade = identidade({
      nome_normalizado: row.nome_normalizado,
      chapa: row.chapa,
      telefone: row.telefone,
    });
    const jaIncluso = group.passageiros.some(
      (passageiro) => identidade(passageiro) === rowIdentidade
    );
    if (jaIncluso) continue;

    group.passageiros.push({
      nome: row.nome,
      nome_normalizado: row.nome_normalizado,
      chapa: row.chapa,
      telefone: row.telefone,
      horario: row.horario,
      origem: row.origem,
      destino: row.destino,
    });
  }

  return [...map.values()].map((group) => {
    const horarios = collectEmbraerHorarios(group);
    return {
      ...group,
      horarios,
      horario: horarios[0] || group.horario || null,
    };
  }).sort((a, b) => {
    const dateCmp = String(a.data || "").localeCompare(String(b.data || ""));
    if (dateCmp !== 0) return dateCmp;
    return String(a.horario || "").localeCompare(String(b.horario || ""));
  });
};

export async function parseEmbraerWorkbookFromFile(file) {
  await validateWorkbookFile(file);
  const xlsx = await import("xlsx");
  const buffer = await file.arrayBuffer();
  const workbook = xlsx.read(buffer, {
    type: "array",
    dense: true,
    cellDates: true,
    cellFormula: false,
    cellHTML: false,
    cellNF: false,
    cellStyles: false,
    raw: true,
    sheetRows: MAX_EMBRAER_ROWS + 1,
  });
  const sheetName = workbook.SheetNames?.[0];
  if (!sheetName) throw new Error("A planilha não possui abas legíveis.");

  const matrix = xlsx.utils.sheet_to_json(workbook.Sheets[sheetName], {
    header: 1,
    raw: true,
    defval: "",
    blankrows: false,
  });
  assertMatrixLimits(matrix);

  const parsed = buildEmbraerRowsFromMatrix(matrix, xlsx);
  return {
    total_linhas: parsed.rows.length,
    total_linhas_ignoradas: parsed.total_linhas_ignoradas,
    programacoes_ignoradas: parsed.programacoes_ignoradas,
    programacoes: groupEmbraerRows(parsed.rows),
  };
}
